import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { changeAntdTheme } from 'dynamic-antd-theme';
import KeycloakContext from '@providers/Keycloak/KeycloakContext';
import useCustomers from '@hooks/useCustomers';
import rolesConstants from '@utils/rolesConstants';
import {
  ICustomerContextOption,
  ICustomersSwitch,
} from '@components/SelectContext/selectContext.types';
import useSwr from '@hooks/useSwr';
import api from '@services/api';
import { ApplicationsProps, useApps } from '@config/useApps';
import type { RouteItemProps } from '@config/routes/types';
import {
  ApplicationsConstants,
  motProducts,
} from '@utils/applicationsConstants';
import useUserPreferences from '@hooks/useUserPreferences';
import { useLocation } from 'react-router-dom';
import usePersistedState from '@hooks/usePersistedState';
import { ThemeProvider } from 'styled-components';
import type { DefaultTheme } from 'styled-components';
import { lightTheme } from '@styles/themes';
import { usePersistentCookie } from '@hooks/usePersistentCookie';
import { SmsAvailableOptions } from '@pages/Sms/SmsRequests';
import { LEGACY_LORAWAN_PAGES, NEW_LORAWAN_PAGES } from '@pages/LoraWan';
import SessionContext from './SessionContext';

type LoraRouteItemProps = RouteItemProps & {
  key?: (typeof NEW_LORAWAN_PAGES)[number];
};

interface SessionProviderProps {
  children: React.ReactNode;
}

const SessionProvider = ({ children }: SessionProviderProps) => {
  const { pathname } = useLocation();
  const { keycloak } = useContext(KeycloakContext);

  const [effectiveRoles, setEffectiveRoles] = useState();
  const [userLogged, setUserLogged] = useState<any>();
  const [mainCustomerInContext, setMainCustomerInContext] =
    useState<ICustomerContextOption>();
  const [customersSwitch, setCustomersSwitch] = useState<ICustomersSwitch>();
  const [isContextSwitch, setIsContextSwitch] = useState(false);
  const [logoImgName, setLogoImgName] = useState<string>();
  const [domainCustomerName, setDomainCustomerName] = useState<string>();
  const [mainColor, setMainColor] = useState<string>();
  const [whatsapp, setWhatsapp] = useState<string>();
  const [whatsappMessage, setWhatsappMessage] = useState<string>();
  const [selectedApp, setSelectedApp] = useState<ApplicationsConstants>();
  const [allowedPaths, setAllowedPaths] = useState<string[] | undefined>();
  const [tourGuideRoute, setTourGuideRoute] = useState('');
  const [personalizationCompleted, setPersonalizationCompleted] =
    useState(false);
  const [showSmsSender, setShowSmsSender] = useState(false);
  const [graphThemeKey, setGraphThemeKey] = useState<
    'primary' | 'secondary' | 'tertiary' | 'quaternary'
  >('primary');

  const themeWithMainColor = useCallback(
    (theme: DefaultTheme): DefaultTheme => ({
      ...theme,
      colors: {
        ...theme?.colors,
        primary: mainColor ?? theme?.colors?.primary,
      },
    }),
    [mainColor],
  );

  const [theme, setTheme] = usePersistedState<DefaultTheme>(
    'theme',
    themeWithMainColor(lightTheme),
  );

  const { getCookie, addCookie } = usePersistentCookie();

  useEffect(() => {
    const themeInCookie = getCookie('theme');
    const currentTheme = JSON.stringify(theme);
    if (!themeInCookie || themeInCookie !== currentTheme) {
      addCookie('theme', JSON.stringify(theme));
    }
  }, [addCookie, getCookie, theme]);

  const isMot = !!(selectedApp && motProducts.includes(selectedApp));
  const customerType = keycloak.idTokenParsed?.customerType;
  const isRoot = customerType === 'ROOT';

  const { data: userSettings, mutate: mutateUserSettings } = useSwr(
    '/users/self/settings/',
  );

  const { data: motOperatorsData } = useSwr(
    isMot ? '/service-proxy/mot-operators' : null,
  );

  const { data: smsAvailableOptions } = useSwr<SmsAvailableOptions>(
    selectedApp === ApplicationsConstants.SMS
      ? `/service-proxy/sms-available-options`
      : null,
  );

  useEffect(
    () => setShowSmsSender(smsAvailableOptions?.sender ?? false),
    [smsAvailableOptions?.sender, setShowSmsSender],
  );

  const hasRoles = useMemo(
    () => effectiveRoles !== undefined,
    [effectiveRoles],
  );

  const domain = useMemo(() => {
    const { hostname } = window.location;

    return hostname === 'localhost'
      ? process.env.REACT_APP_HML_HOSTNAME
      : hostname;
  }, []);

  const algarDomains = useMemo(
    () =>
      [
        'localhost',
        process.env.REACT_APP_HML_HOSTNAME,
        'iot.algar.com.br',
      ].filter((candidate): candidate is string => candidate !== undefined),
    [],
  );

  const isAlgarDomain = useMemo(() => {
    const { hostname } = window.location;
    return algarDomains.includes(hostname);
  }, [algarDomains]);

  useEffect(() => {
    const isDarkTheme = theme?.title === 'dark';
    setLogoImgName(isDarkTheme ? `${domain}-dark` : domain);
  }, [domain, theme?.title]);

  useEffect(() => {
    if (mainColor && mainColor !== theme?.colors?.primary) {
      changeAntdTheme(mainColor);
      setTheme(crrTheme => themeWithMainColor(crrTheme));
    }
  }, [mainColor, setTheme, theme?.colors?.primary, themeWithMainColor]);

  useEffect(() => {
    if (hasRoles) {
      if (isAlgarDomain) {
        setMainColor(theme?.colors.primary);
        setPersonalizationCompleted(true);
      } else {
        api
          .get('/service-proxy/broker/theme', {
            params: {
              domain,
            },
          })
          .then(({ data }) => {
            const isValidString = (str: string) =>
              str && str !== '' && str !== '#FFF';

            const isDarkTheme = theme?.title === 'dark';
            const color = isDarkTheme ? data?.darkMainColor : data?.mainColor;
            const hasColor = isValidString(color);

            if (hasColor) {
              changeAntdTheme(color);
              setMainColor(color);
            }
            const treatedNullValue = (value?: string) =>
              value !== null ? value : undefined;
            setWhatsapp(treatedNullValue(data?.whatsapp));
            setWhatsappMessage(treatedNullValue(data?.whatsappMessage));
            setDomainCustomerName(data?.name);
            setPersonalizationCompleted(true);
          });
      }
    }
  }, [domain, hasRoles, isAlgarDomain, mainColor, theme, themeWithMainColor]);

  const isAllcom = useMemo(
    () => customersSwitch?.selected === 10000,
    [customersSwitch],
  );

  const motOperators = useMemo(
    () => motOperatorsData?.map(({ name }: { name: string }) => name),
    [motOperatorsData],
  );

  const changeGuideRoute = useCallback(
    (routeName: string) => setTourGuideRoute(routeName),
    [],
  );

  const getCustomersSwitch = useCallback(
    () => api.get('/users/customers-switch/'),
    [],
  );

  const getEffectiveRoles = useCallback(
    () => api.get('/users/effective-roles/'),
    [],
  );

  const setUserSettings = useCallback(
    async (newSettings: any) => {
      await api.put('/users/self/settings/', newSettings).then(() => {
        const newUserSettings = userSettings;
        Object.entries(newSettings)?.forEach(([attrName, attrValue]) => {
          // @ts-ignore
          newUserSettings[attrName] = attrValue;
        });
        mutateUserSettings(newUserSettings);
      });
    },
    [mutateUserSettings, userSettings],
  );

  useEffect(() => {
    // @ts-ignore
    const pathnameSplit: products = pathname
      .split('/')
      .filter(item => item !== '')[0];
    setSelectedApp(pathnameSplit);
  }, [pathname]);

  useEffect(() => {
    if (keycloak) {
      // @ts-ignore
      setUserLogged(keycloak?.idTokenParsed);
    }
  }, [keycloak]);

  const hasPermission = useCallback(
    // @ts-ignore
    (role, client = 'portal') => effectiveRoles?.[client]?.includes(role),
    [effectiveRoles],
  );

  const hasCustomersRoles = useMemo(() => {
    let hasRole = false;
    [
      rolesConstants.MANAGE_CUSTOMERS,
      rolesConstants.VIEW_CUSTOMERS,
      rolesConstants.MANAGE_DEPARTMENTS,
      rolesConstants.VIEW_DEPARTAMENTS,
    ].forEach(role => {
      // @ts-ignore
      if (effectiveRoles?.portal?.includes(role)) {
        hasRole = true;
      }
    });

    return hasRole;
  }, [effectiveRoles]);

  // Customer Switch, get new data and set
  const setCustomerContext = useCallback(async () => {
    const { data: roles } = await getEffectiveRoles();
    // @ts-ignore
    setEffectiveRoles(roles);
    getCustomersSwitch().then(({ data }: any) => setCustomersSwitch(data));
  }, [
    getEffectiveRoles,
    setEffectiveRoles,
    getCustomersSwitch,
    setCustomersSwitch,
  ]);

  useEffect(() => {
    if (!effectiveRoles && !customersSwitch) {
      setCustomerContext();
    }
  }, [effectiveRoles, customersSwitch, setCustomerContext]);

  const customer = useCustomers(hasCustomersRoles, hasRoles);
  const userPreferences = useUserPreferences(
    userLogged?.user_id,
    userLogged?.customerId,
  );
  const listContracts = customer?.customerLogged?.listContractTypes?.filter(
    (item: string) => item !== 'MOT',
  );
  const hasOnlyMotBusiness =
    listContracts?.length === 1 &&
    listContracts?.includes(ApplicationsConstants.MOT_BUSINESS.toUpperCase());
  const hasOnlyMotEnterprise =
    listContracts?.length === 1 &&
    listContracts?.includes(ApplicationsConstants.MOT_ENTERPRISE.toUpperCase());
  const isMotBusiness = selectedApp === ApplicationsConstants.MOT_BUSINESS;
  const isMotPay = selectedApp === ApplicationsConstants.MOT_PAY;
  const isMotEnterprise = selectedApp === ApplicationsConstants.MOT_ENTERPRISE;
  const isMotEssential = selectedApp === ApplicationsConstants.MOT_ESSENTIAL;
  const firstMotProduct = listContracts?.find((contract: string) =>
    contract.includes('MOT'),
  );
  const hasMotLorawan = isMotEnterprise || isMotBusiness;

  const isParentCustomer = useMemo(
    () => userLogged?.customerType !== 'CUSTOMER',
    [userLogged?.customerType],
  );

  const isContextSwitchingEnabled = useMemo(() => {
    const isInAnotherContext =
      mainCustomerInContext?.id !== userLogged?.customerId;

    return (
      (isParentCustomer && !isAllcom) ||
      (!isParentCustomer &&
        customersSwitch &&
        customersSwitch?.options?.length > 1) ||
      isInAnotherContext
    );
  }, [
    mainCustomerInContext?.id,
    userLogged,
    isParentCustomer,
    isAllcom,
    customersSwitch,
  ]);

  const isNotChildOfRoot = useMemo(
    () => Boolean(customer?.customerLogged?.parentId),
    [customer?.customerLogged?.parentId],
  );

  const handlePagePath = useCallback(
    (
      appName: string,
      categoryName: string,
      pathName: string,
      hasLorawan?: boolean,
      isLoraWan?: boolean,
      isCommon?: boolean,
    ) => {
      const hasCategory = categoryName !== appName;

      const newPagePath = (basePath?: string) => {
        const treatedBasePath = basePath ? `/${basePath}` : '';
        return `/${appName}${treatedBasePath}/${
          hasCategory ? `${categoryName}/` : ''
        }${pathName}`;
      };

      const newPagePaths: string[] = [];
      if (hasLorawan) {
        if (isCommon) {
          newPagePaths.push(
            newPagePath('mobile-network'),
            newPagePath('lorawan'),
          );
        } else {
          const deviceType = isLoraWan ? 'lorawan' : 'mobile-network';
          newPagePaths.push(newPagePath(deviceType));
        }
      } else {
        newPagePaths.push(newPagePath());
      }

      setAllowedPaths(oldAllowedPaths => [
        ...new Set(
          oldAllowedPaths
            ? [...oldAllowedPaths, ...newPagePaths]
            : newPagePaths,
        ),
      ]);
    },
    [],
  );

  const { applications } = useApps();

  const pagesRouting: ApplicationsProps[] = useMemo(() => {
    const allowedApps = applications?.filter(app => {
      const isNoChildOfRootAndIsFinancialApp =
        isNotChildOfRoot && app.name === ApplicationsConstants.FINANCIAL;
      const isSms = app.name === ApplicationsConstants.SMS;

      // Allcom does not need to see MoT Enterprise
      const isAllcomAndIsMotEnterprise =
        isAllcom && app.name === ApplicationsConstants.MOT_ENTERPRISE;

      if (
        isNoChildOfRootAndIsFinancialApp ||
        isAllcomAndIsMotEnterprise ||
        (isRoot && isSms)
      ) {
        return false;
      }

      // if (isSms) {
      //   return hasPermission(rolesConstants.VIEW_SMS, 'portal');
      // }
      return customer?.customerApplications?.includes(app.name);
    });

    return allowedApps?.map(app => {
      const isNotChildAndIsMoT =
        isNotChildOfRoot && motProducts.includes(app.name);
      const appType = isNotChildAndIsMoT ? 'app' : app.type;

      return {
        ...app,
        type: appType,
        categories: app.categories?.map(category => {
          const pages = category.pages?.filter(page => {
            const isLora = app.name === ApplicationsConstants.LORAWAN;
            const isMotLora = isMot && app.hasLorawan && page.isLoraWan;
            if (isLora || isMotLora) {
              const hasNewLora = hasPermission(rolesConstants.LORAWAN_NEW);
              const loraPageKey = (page as LoraRouteItemProps).key;

              if (loraPageKey) {
                const availablePages = hasNewLora
                  ? NEW_LORAWAN_PAGES
                  : LEGACY_LORAWAN_PAGES;

                if (!availablePages.includes(loraPageKey)) {
                  return false;
                }
              }
            }

            if (
              (isNotChildOfRoot && page.onlyChildOfRoot) ||
              (isAllcom && page.path === 'customers') ||
              ((hasOnlyMotBusiness || hasOnlyMotEnterprise) &&
                page.application?.includes(ApplicationsConstants.ACCESS) &&
                page.path === 'customers') ||
              (page.path === 'senders' && !showSmsSender) ||
              (isRoot && page.hideWhenRoot)
            ) {
              return false;
            }
            if (hasPermission(page.role) || page?.isDetails || !page.role) {
              if (!page.isDetails) {
                handlePagePath(
                  app.name,
                  category.categoryName,
                  page.path,
                  app?.hasLorawan,
                  page?.isLoraWan,
                  page?.isCommon,
                );
              }

              return page;
            }

            return false;
          });

          return {
            ...category,
            pages,
          };
        }),
      };
    });
  }, [
    applications,
    customer?.customerApplications,
    handlePagePath,
    hasOnlyMotBusiness,
    hasOnlyMotEnterprise,
    hasPermission,
    isAllcom,
    isNotChildOfRoot,
    isRoot,
    isMot,
    showSmsSender,
  ]);

  const allowedPagesRouting: ApplicationsProps[] = useMemo(() => {
    const itemsWithPages = pagesRouting.filter(item =>
      item.categories.some(category => {
        const pageWithoutDetails = category?.pages?.filter(
          page => !page?.isDetails,
        );
        return pageWithoutDetails?.length > 0;
      }),
    );

    return itemsWithPages;
  }, [pagesRouting]);

  const getAppByName = useCallback(
    (appName: string | undefined) =>
      allowedPagesRouting?.find(item => item.name === appName),
    [allowedPagesRouting],
  );

  const value = useMemo(
    () => ({
      effectiveRoles,
      setEffectiveRoles,
      hasPermission,
      mainCustomerInContext,
      setMainCustomerInContext,
      isContextSwitchingEnabled,
      userLogged,
      hasCustomersRoles,
      customer,
      customersSwitch,
      setCustomersSwitch,
      isContextSwitch,
      setIsContextSwitch,
      userSettings,
      setUserSettings,
      logoImgName,
      setLogoImgName,
      mainColor,
      setMainColor,
      selectedApp,
      setSelectedApp,
      allowedPagesRouting,
      getAppByName,
      allowedPaths,
      tourGuideRoute,
      changeGuideRoute,
      mutateUserSettings,
      isNotChildOfRoot,
      isParentCustomer,
      isMot,
      motOperators,
      userPreferences,
      isAllcom,
      isMotBusiness,
      isMotPay,
      isRoot,
      hasOnlyMotBusiness,
      whatsapp,
      whatsappMessage,
      isAlgarDomain,
      algarDomains,
      domainCustomerName,
      isMotEnterprise,
      personalizationCompleted,
      theme,
      setTheme,
      themeWithMainColor,
      firstMotProduct,
      hasMotLorawan,
      isMotEssential,
      smsAvailableOptions,
      graphThemeKey,
      setGraphThemeKey,
    }),
    [
      effectiveRoles,
      hasPermission,
      mainCustomerInContext,
      isContextSwitchingEnabled,
      userLogged,
      hasCustomersRoles,
      customer,
      customersSwitch,
      isContextSwitch,
      userSettings,
      setUserSettings,
      logoImgName,
      mainColor,
      selectedApp,
      allowedPagesRouting,
      getAppByName,
      allowedPaths,
      tourGuideRoute,
      changeGuideRoute,
      mutateUserSettings,
      isNotChildOfRoot,
      isParentCustomer,
      isMot,
      motOperators,
      userPreferences,
      isAllcom,
      isMotBusiness,
      isMotPay,
      isRoot,
      hasOnlyMotBusiness,
      whatsapp,
      whatsappMessage,
      isAlgarDomain,
      algarDomains,
      domainCustomerName,
      isMotEnterprise,
      personalizationCompleted,
      theme,
      setTheme,
      themeWithMainColor,
      firstMotProduct,
      hasMotLorawan,
      isMotEssential,
      smsAvailableOptions,
      graphThemeKey,
    ],
  );

  return (
    <SessionContext.Provider value={value}>
      {/* @ts-expect-error  */}
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </SessionContext.Provider>
  );
};

export default SessionProvider;
